import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function DriverLogin() {
  const { user, login, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user && user.role === "driver") {
    return <Navigate to="/driver" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const result = await login(email.trim().toLowerCase(), password);
      if (result?.user?.role !== "driver") {
        logout();
        setError("Driver credentials required.");
        return;
      }
      navigate("/driver", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="driver-login-page"
      className="min-h-screen bg-[#0B0B0C] flex flex-col px-6"
    >
      <div className="flex-1 flex flex-col items-center justify-center max-w-sm mx-auto w-full">
        <div className="text-center mb-10">
          <div className="font-serif text-3xl font-semibold tracking-tight text-white">
            Phantasm
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.4em] text-[#8E9AA6]">
            Chauffeur
          </div>
        </div>

        <form onSubmit={onSubmit} data-testid="driver-login-form" className="w-full space-y-8">
          <div>
            <label className="block text-xs uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
              Email
            </label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="driver-login-email-input"
              autoComplete="email"
              inputMode="email"
              className="w-full bg-transparent border-b border-white/20 px-0 py-4 text-white text-lg focus:border-white focus:outline-none transition-colors font-sans"
              placeholder="driver@phantasm.com"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="driver-login-password-input"
              autoComplete="current-password"
              className="w-full bg-transparent border-b border-white/20 px-0 py-4 text-white text-lg focus:border-white focus:outline-none transition-colors font-sans"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div
              data-testid="driver-login-error"
              className="text-sm text-red-400 tracking-wide"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="driver-login-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-50 px-8 py-5 transition-all duration-300 font-medium text-sm tracking-[0.25em] uppercase"
          >
            {submitting ? (
              <>
                <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />
                Signing in…
              </>
            ) : (
              <>Sign in</>
            )}
          </button>
        </form>
      </div>

      <div className="pb-8 text-center">
        <Link
          to="/"
          data-testid="driver-login-back"
          className="text-[10px] uppercase tracking-[0.35em] text-[#8E9AA6] hover:text-white"
        >
          ← Client site
        </Link>
      </div>
    </div>
  );
}
